import { useMemo, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Empty, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { DeliveryBadge, DeliveryTrackingDialog } from "@/components/DeliveryTracking";
import {
  MagnifyingGlass, PaperPlaneTilt, CheckCircle, Receipt, UploadSimple,
  DotsThreeVertical, FileText, Translate, Prohibit, Trash, Warning, Plus, Package,
} from "@phosphor-icons/react";

const STATUS_LABEL = {
  pending: "Draft",
  quote_sent: "Quote sent",
  quote_accepted: "In queue",
  in_progress: "In progress",
  completed: "Delivered",
  invoiced: "Invoiced",
  cancelled: "Cancelled",
};
const STATUS_TONE = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  quote_sent: "bg-blue-50 text-blue-700 border-blue-200",
  quote_accepted: "bg-purple-50 text-purple-700 border-purple-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoiced: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const priceLabel = (p) => {
  const m = p.pricing_model || "flat";
  if (m === "per_word") return `${(p.word_count || 0).toLocaleString()} words × $${(p.per_unit_rate || 0).toFixed(4)}/word`;
  if (m === "per_page") return `${p.page_count || 0} pages × $${(p.per_unit_rate || 0).toFixed(2)}/page`;
  if (m === "hourly") return `${p.duration_hours || 0} hrs × $${(p.per_unit_rate || 0).toFixed(2)}/hr`;
  return `Flat rate`;
};

const computedTotal = (p) => {
  const m = p.pricing_model || "flat";
  const n = (v) => parseFloat(v || 0) || 0;
  if (m === "per_word") return n(p.word_count) * n(p.per_unit_rate);
  if (m === "per_page") return n(p.page_count) * n(p.per_unit_rate);
  if (m === "hourly")   return n(p.duration_hours) * n(p.per_unit_rate);
  return n(p.flat_rate);
};

const emptyForm = {
  title: "", client_id: "", source_language: "", target_language: "",
  pricing_model: "flat", word_count: 0, page_count: 0, per_unit_rate: 0,
  flat_rate: 0, duration_hours: 1, due_date: "", notes: "",
};

export default function Translations() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fLangPair, setFLangPair] = useState("all");
  const [deliverFor, setDeliverFor] = useState(null); // project or null
  const [deliverMsg, setDeliverMsg] = useState("");
  const [markCompleted, setMarkCompleted] = useState(true);
  const [finalInvoiceFor, setFinalInvoiceFor] = useState(null);
  const [trackFor, setTrackFor] = useState(null);
  const [picked, setPicked] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const fileRef = useRef(null);

  const { data: allProjects = [], isLoading } = useQuery({
    queryKey: ["projects", "translations-all"],
    queryFn: async () => (await api.get("/projects", { params: { include_archived: true } })).data,
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.client_id, c])), [clients]);
  // Only clients tagged as translation (or both) are offered when creating a translation project.
  const translationClients = useMemo(
    () => clients.filter((c) => !c.client_type || c.client_type === "translation" || c.client_type === "both"),
    [clients],
  );

  const translations = useMemo(() => allProjects.filter((p) => p.project_type === "Translation"), [allProjects]);

  // Build unique language-pair options from the data.
  const langPairs = useMemo(() => {
    const s = new Set();
    for (const p of translations) {
      const src = p.source_language || "—";
      const tgt = p.target_language || p.language || "—";
      s.add(`${src} → ${tgt}`);
    }
    return Array.from(s).sort();
  }, [translations]);

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return translations.filter((p) => {
      if (fStatus !== "all" && p.status !== fStatus) return false;
      const pair = `${p.source_language || "—"} → ${p.target_language || p.language || "—"}`;
      if (fLangPair !== "all" && pair !== fLangPair) return false;
      if (!q) return true;
      const client = clientById[p.client_id]?.name || "";
      return `${p.title || ""} ${client} ${pair}`.toLowerCase().includes(q);
    });
  }, [translations, search, fStatus, fLangPair, clientById]);

  const stats = useMemo(() => ({
    total: translations.length,
    drafts: translations.filter((p) => p.status === "pending").length,
    inQueue: translations.filter((p) => ["quote_sent", "quote_accepted", "in_progress"].includes(p.status)).length,
    delivered: translations.filter((p) => p.status === "completed").length,
    invoiced: translations.filter((p) => p.status === "invoiced").length,
    totalWords: translations.filter((p) => p.pricing_model === "per_word").reduce((s, p) => s + (p.word_count || 0), 0),
    totalPages: translations.filter((p) => p.pricing_model === "per_page").reduce((s, p) => s + (p.page_count || 0), 0),
  }), [translations]);

  const createTranslation = useMutation({
    mutationFn: async (payload) => (await api.post("/projects", {
      ...payload,
      project_type: "Translation",
      language: payload.target_language,   // legacy field
      status: "pending",
      auto_send_contract: false,           // translations use the send-quote/estimate flow instead
      auto_send_invite: false,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setNewOpen(false);
      setForm(emptyForm);
      toast.success("Translation project created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });

  const sendQuote = useMutation({
    mutationFn: async (project_id) => (await api.post(`/projects/${project_id}/send-quote`, {})).data,
    onSuccess: (d, project_id) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      const p = translations.find((x) => x.project_id === project_id);
      const label = p?.pricing_model === "flat" ? "Estimate" : "Quote";
      toast.success(`${label} emailed to ${d.sent_to} — $${(d.quote_amount || 0).toFixed(2)}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send quote failed"),
  });
  const acceptQuote = useMutation({
    mutationFn: async (project_id) => (await api.post(`/projects/${project_id}/accept-quote`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Quote marked accepted — project is now in the queue");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Accept failed"),
  });
  const setStatus = useMutation({
    mutationFn: async ({ project, next }) => (
      await api.patch(`/projects/${project.project_id}`, {
        title: project.title, client_id: project.client_id, project_type: "Translation",
        pricing_model: project.pricing_model, word_count: project.word_count,
        page_count: project.page_count, per_unit_rate: project.per_unit_rate,
        flat_rate: project.flat_rate, duration_hours: project.duration_hours,
        due_date: project.due_date, source_language: project.source_language,
        target_language: project.target_language, language: project.language,
        status: next,
      })
    ).data,
    onSuccess: (_d, { next }) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Status updated → ${STATUS_LABEL[next] || next}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const sendFinalInvoice = useMutation({
    mutationFn: async ({ project_id, auto_pay_link }) => (
      await api.post(`/projects/${project_id}/send-final-invoice`, { auto_pay_link, origin_url: window.location.origin })
    ).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setFinalInvoiceFor(null);
      toast.success(d.mode === "auto"
        ? `Invoice ${d.invoice_number} emailed with Stripe pay link`
        : `Draft invoice ${d.invoice_number} created — review from Invoices`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invoice failed"),
  });
  const deliverMut = useMutation({
    mutationFn: async ({ project_id, files, message, mark_completed }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (message) fd.append("message", message);
      fd.append("mark_completed", mark_completed ? "true" : "false");
      return (await api.post(`/projects/${project_id}/deliver-translation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["comms"] });
      setDeliverFor(null); setDeliverMsg(""); setPicked([]); if (fileRef.current) fileRef.current.value = "";
      toast.success(d.status === "released"
        ? (d.email_sent ? `${d.files} file(s) added — already paid, download links emailed to ${d.sent_to}` : `${d.files} file(s) released — email failed, use Resend in the menu`)
        : `${d.files} file(s) delivered — preview emailed to ${d.sent_to || "client"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delivery failed"),
  });

  return (
    <div data-testid="translations-page">
      <PageHeader
        eyebrow="Workspace"
        title="Translations"
        description="Manage translation projects — quote, deliver, and invoice. Every step tracked with word/page count and language pair."
        actions={
          <Button onClick={() => { setForm(emptyForm); setNewOpen(true); }} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="new-translation-btn">
            <Plus size={16} weight="bold" className="mr-1.5" /> New translation
          </Button>
        }
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Drafts" value={stats.drafts} tone="slate" />
        <StatCard label="In queue" value={stats.inQueue} tone="amber" />
        <StatCard label="Delivered" value={stats.delivered} tone="emerald" />
        <StatCard label="Invoiced" value={stats.invoiced} tone="indigo" />
        <StatCard label="Total volume" value={
          stats.totalWords > 0 || stats.totalPages > 0
            ? `${stats.totalWords.toLocaleString()}w · ${stats.totalPages}p`
            : "—"
        } small />
      </div>

      {/* Filters */}
      <div className="card-flat p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="translation-filters">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by title, client, or language…" className="pl-9"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="translation-search" />
        </div>
        <div className="min-w-[170px]">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {langPairs.length > 0 && (
          <div className="min-w-[180px]">
            <Select value={fLangPair} onValueChange={setFLangPair}>
              <SelectTrigger data-testid="filter-langpair"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All languages</SelectItem>
                {langPairs.map((lp) => <SelectItem key={lp} value={lp}>{lp}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-xs text-slate-500 ml-auto">
          Showing <b>{displayed.length}</b> of <b>{translations.length}</b>
        </div>
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : translations.length === 0 ? (
        <Empty
          title="No translation projects yet"
          hint="Create a project with Type = Translation from the Projects page. Word / page count and language pair will appear here."
        />
      ) : displayed.length === 0 ? (
        <Empty title="No translations match" hint="Try clearing filters or a different search term." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="translations-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Project</th>
                <th className="text-left px-3 py-3 font-semibold">Language pair</th>
                <th className="text-left px-3 py-3 font-semibold">Volume</th>
                <th className="text-left px-3 py-3 font-semibold">Pricing</th>
                <th className="text-right px-3 py-3 font-semibold">Total</th>
                <th className="text-left px-3 py-3 font-semibold">Due</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((p) => {
                const client = clientById[p.client_id];
                const src = p.source_language || "—";
                const tgt = p.target_language || p.language || "—";
                const total = p.quote_amount || computedTotal(p);
                const overdue = p.due_date && new Date(p.due_date) < new Date() && !["completed", "invoiced", "cancelled"].includes(p.status);
                return (
                  <tr key={p.project_id} className="data-row border-t border-slate-100" data-testid={`translation-row-${p.project_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{p.title}</div>
                      <div className="text-xs text-slate-500">{client?.name || "—"}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700 text-xs whitespace-nowrap">
                      <Translate size={12} className="inline mr-1 text-slate-400" />
                      {src} <span className="text-slate-400">→</span> {tgt}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-700 whitespace-nowrap">
                      {p.pricing_model === "per_word" && <><b>{(p.word_count || 0).toLocaleString()}</b> words</>}
                      {p.pricing_model === "per_page" && <><b>{p.page_count || 0}</b> pages</>}
                      {p.pricing_model === "hourly" && <><b>{p.duration_hours || 0}</b> hrs</>}
                      {p.pricing_model === "flat" && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{priceLabel(p)}</td>
                    <td className="px-3 py-3 text-right font-mono text-sm">{fmtMoney(total)}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">
                      {p.due_date ? (
                        <span className={overdue ? "text-red-600 font-semibold" : "text-slate-700"}>
                          {overdue && <Warning size={11} className="inline mr-0.5" />}
                          {fmtDate(p.due_date)}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`badge-pill ${STATUS_TONE[p.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                      <div><DeliveryBadge project={p} /></div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`translation-actions-${p.project_id}`}>
                            <DotsThreeVertical size={16} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-white">
                          <DropdownMenuLabel className="text-xs text-slate-500">Workflow</DropdownMenuLabel>
                          {(p.status === "pending" || p.status === "quote_sent") && (
                            <DropdownMenuItem onClick={() => sendQuote.mutate(p.project_id)} data-testid={`send-quote-${p.project_id}`}>
                              <PaperPlaneTilt size={14} className="mr-2" />
                              {p.pricing_model === "flat"
                                ? (p.status === "quote_sent" ? "Resend estimate" : "Send estimate to client")
                                : (p.status === "quote_sent" ? "Resend quote" : "Send quote to client")}
                            </DropdownMenuItem>
                          )}
                          {p.status === "quote_sent" && (
                            <DropdownMenuItem onClick={() => acceptQuote.mutate(p.project_id)} className="text-emerald-700" data-testid={`accept-quote-${p.project_id}`}>
                              <CheckCircle size={14} className="mr-2" /> {p.pricing_model === "flat" ? "Mark estimate accepted" : "Mark quote accepted"}
                            </DropdownMenuItem>
                          )}
                          {p.status === "quote_accepted" && (
                            <DropdownMenuItem onClick={() => setStatus.mutate({ project: p, next: "in_progress" })} data-testid={`mark-in-progress-${p.project_id}`}>
                              <FileText size={14} className="mr-2" /> Mark in progress
                            </DropdownMenuItem>
                          )}
                          {["quote_accepted", "in_progress", "completed", "invoiced"].includes(p.status) && (
                            <DropdownMenuItem
                              onClick={() => { setDeliverFor(p); setDeliverMsg(""); setPicked([]); setMarkCompleted(true); }}
                              data-testid={`deliver-${p.project_id}`}
                            >
                              <UploadSimple size={14} className="mr-2" /> {(p.deliveries || []).length ? "Add more files…" : "Upload & deliver files…"}
                            </DropdownMenuItem>
                          )}
                          {["completed", "in_progress", "quote_accepted"].includes(p.status) && (
                            <DropdownMenuItem onClick={() => setFinalInvoiceFor(p)} data-testid={`send-final-invoice-${p.project_id}`}>
                              <Receipt size={14} className="mr-2" /> Send final invoice…
                            </DropdownMenuItem>
                          )}
                          {["quote_accepted", "in_progress", "invoiced"].includes(p.status) && (
                            <DropdownMenuItem
                              onClick={() => setStatus.mutate({ project: p, next: "completed" })}
                              className="text-emerald-700"
                              data-testid={`mark-completed-${p.project_id}`}
                            >
                              <CheckCircle size={14} className="mr-2" /> Mark completed
                            </DropdownMenuItem>
                          )}
                          {(p.deliveries || []).length > 0 && (
                            <DropdownMenuItem onClick={() => setTrackFor(p)} data-testid={`delivery-tracking-${p.project_id}`}>
                              <Package size={14} className="mr-2" />
                              {p.awaiting_payment_release ? "Delivery status / release…" : "Resend translation / tracking…"}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {p.status !== "cancelled" && (
                            <DropdownMenuItem
                              onClick={() => {
                                if (window.confirm(`Cancel "${p.title}"?`)) setStatus.mutate({ project: p, next: "cancelled" });
                              }}
                              className="text-amber-700"
                              data-testid={`cancel-${p.project_id}`}
                            >
                              <Prohibit size={14} className="mr-2" /> Cancel project
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload & deliver dialog */}
      <Dialog open={!!deliverFor} onOpenChange={(o) => { if (!o) { setDeliverFor(null); setDeliverMsg(""); setPicked([]); if (fileRef.current) fileRef.current.value = ""; } }}>
        <DialogContent className="bg-white" data-testid="deliver-dialog">
          <DialogHeader>
            <DialogTitle>Upload & deliver — {deliverFor?.title}</DialogTitle>
          </DialogHeader>
          {deliverFor && (
            <div className="text-sm space-y-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Client</span><b>{clientById[deliverFor.client_id]?.name || "—"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-mono">{clientById[deliverFor.client_id]?.email || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Language pair</span><b>{deliverFor.source_language || "—"} → {deliverFor.target_language || deliverFor.language || "—"}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Volume</span><b>{priceLabel(deliverFor)}</b></div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Completed files (select one or more · max 25 MB each)</div>
                <Input type="file" multiple ref={fileRef} onChange={(e) => setPicked(Array.from(e.target.files || []))} data-testid="deliver-file" />
                {picked.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-slate-700" data-testid="deliver-file-list">
                    {picked.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex justify-between gap-2 px-2 py-1 rounded bg-slate-50 border border-slate-200">
                        <span className="truncate">{f.name}</span>
                        <span className="text-slate-400 shrink-0">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Message to client (optional)</div>
                <Textarea
                  rows={3}
                  value={deliverMsg}
                  onChange={(e) => setDeliverMsg(e.target.value)}
                  placeholder="e.g. Please review the attached translation and reply if you need any changes."
                  data-testid="deliver-message"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markCompleted}
                  onChange={(e) => setMarkCompleted(e.target.checked)}
                  data-testid="deliver-mark-completed"
                />
                Mark project as delivered (unlocks "Send final invoice")
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliverFor(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const files = Array.from(fileRef.current?.files || []);
                if (!files.length) return toast.error("Pick at least one file");
                deliverMut.mutate({ project_id: deliverFor.project_id, files, message: deliverMsg, mark_completed: markCompleted });
              }}
              disabled={deliverMut.isPending}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              data-testid="deliver-confirm-btn"
            >
              {deliverMut.isPending ? "Sending…" : picked.length > 1 ? `Upload ${picked.length} files & email client` : "Upload & email to client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeliveryTrackingDialog project={trackFor} clientEmail={clientById[trackFor?.client_id]?.email} onClose={() => setTrackFor(null)} />

      {/* Final invoice dialog */}
      <Dialog open={!!finalInvoiceFor} onOpenChange={(o) => !o && setFinalInvoiceFor(null)}>        <DialogContent className="bg-white" data-testid="final-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Send final invoice — {finalInvoiceFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-3">
            <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Invoice amount</span>
                <span className="font-bold text-lg text-slate-900">{fmtMoney(finalInvoiceFor?.quote_amount || computedTotal(finalInvoiceFor || {}))}</span>
              </div>
              {finalInvoiceFor?.due_date && (
                <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-200">
                  <span className="uppercase tracking-wider text-slate-500 font-semibold">Delivered by</span>
                  <span className="text-slate-900">{fmtDate(finalInvoiceFor.due_date)}</span>
                </div>
              )}
            </div>
            <p>Pick how you'd like this invoice to go out:</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline"
              onClick={() => finalInvoiceFor && sendFinalInvoice.mutate({ project_id: finalInvoiceFor.project_id, auto_pay_link: false })}
              disabled={sendFinalInvoice.isPending}
              data-testid="final-invoice-draft"
            >
              Create draft (I'll review first)
            </Button>
            <Button
              onClick={() => finalInvoiceFor && sendFinalInvoice.mutate({ project_id: finalInvoiceFor.project_id, auto_pay_link: true })}
              disabled={sendFinalInvoice.isPending}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              data-testid="final-invoice-auto"
            >
              {sendFinalInvoice.isPending ? "Sending…" : "Email now with Stripe pay link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- New translation sheet --- */}
      <Sheet open={newOpen} onOpenChange={setNewOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle>New translation project</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4" data-testid="new-translation-form">
            <TField label="Project title">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Marriage certificate translation" data-testid="new-tr-title" />
            </TField>

            <TField label="Client">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="new-tr-client"><SelectValue placeholder="Select translation client…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {translationClients.length === 0 && (
                    <div className="text-xs text-slate-500 px-3 py-2">
                      No translation clients yet — set "Services" to Translation or Both on a client to see them here.
                    </div>
                  )}
                  {translationClients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>
                      {c.name}{c.client_type === "both" ? "  ·  (both)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">Only clients tagged as Translation or Both appear here.</div>
            </TField>

            <div className="grid grid-cols-2 gap-3">
              <TField label="Source language"><Input value={form.source_language} onChange={(e) => setForm({ ...form, source_language: e.target.value })} placeholder="French" data-testid="new-tr-src" /></TField>
              <TField label="Target language"><Input value={form.target_language} onChange={(e) => setForm({ ...form, target_language: e.target.value })} placeholder="English" data-testid="new-tr-tgt" /></TField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TField label="Pricing model">
                <Select value={form.pricing_model} onValueChange={(v) => setForm({ ...form, pricing_model: v })}>
                  <SelectTrigger data-testid="new-tr-pricing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_word">Per word</SelectItem>
                    <SelectItem value="per_page">Per page</SelectItem>
                    <SelectItem value="flat">Flat rate (estimate)</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </TField>
              <TField label="Delivery due date">
                <Input type="date" value={form.due_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="new-tr-due" />
              </TField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {form.pricing_model === "per_word" && (
                <>
                  <TField label="Word count"><Input type="number" value={form.word_count} onChange={(e) => setForm({ ...form, word_count: parseFloat(e.target.value || 0) })} data-testid="new-tr-word-count" /></TField>
                  <TField label="Rate ($/word)"><Input type="number" step="0.0001" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="new-tr-per-unit-rate" /></TField>
                </>
              )}
              {form.pricing_model === "per_page" && (
                <>
                  <TField label="Page count"><Input type="number" step="0.5" value={form.page_count} onChange={(e) => setForm({ ...form, page_count: parseFloat(e.target.value || 0) })} data-testid="new-tr-page-count" /></TField>
                  <TField label="Rate ($/page)"><Input type="number" step="0.01" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="new-tr-per-unit-rate" /></TField>
                </>
              )}
              {form.pricing_model === "hourly" && (
                <>
                  <TField label="Hours"><Input type="number" step="0.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseFloat(e.target.value || 0) })} data-testid="new-tr-hours" /></TField>
                  <TField label="Rate ($/hr)"><Input type="number" step="0.01" value={form.per_unit_rate} onChange={(e) => setForm({ ...form, per_unit_rate: parseFloat(e.target.value || 0) })} data-testid="new-tr-per-unit-rate" /></TField>
                </>
              )}
              {form.pricing_model === "flat" && (
                <TField label="Flat rate ($)"><Input type="number" step="0.01" value={form.flat_rate} onChange={(e) => setForm({ ...form, flat_rate: parseFloat(e.target.value || 0) })} data-testid="new-tr-flat-rate" /></TField>
              )}
            </div>

            <div className="flex items-center justify-between p-2 rounded bg-slate-50 border border-slate-200">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                {form.pricing_model === "flat" ? "Estimated total" : "Quote total"}
              </span>
              <span className="font-bold text-lg text-slate-900" data-testid="new-tr-total">${computedTotal(form).toFixed(2)}</span>
            </div>

            <TField label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></TField>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setNewOpen(false)} className="flex-1">Cancel</Button>
              <Button
                onClick={() => createTranslation.mutate(form)}
                disabled={createTranslation.isPending || !form.title || !form.client_id}
                className="flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                data-testid="new-tr-save"
              >
                {createTranslation.isPending ? "Creating…" : "Create project"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function TField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StatCard({ label, value, tone = "slate", small }) {
  const toneCls = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    indigo: "text-indigo-700",
  }[tone] || "text-slate-900";
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`${small ? "text-sm" : "text-2xl"} font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}
